import { RootState } from "../store";

import { useSelector } from "react-redux";

import { useCallback, useMemo, useRef, useEffect } from "react";
import { AppDispatch } from "../store";
import { useDispatch } from "react-redux";
import { getCallStreamDataFromCall, streamService } from "../utils/StreamService";
import { AuthenticationStatusGetStream, setCurrentCallData, setNbUnread, setStatusGetStream, CallStreamData, removeIdsCallsIgnored, addIdsCallsIgnored } from "../store/slices/authSlice";
import { authRepository } from "../repositories/AuthRepository";
import { Call } from "@stream-io/video-react-sdk";

export function useStream() {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const dispatch = useDispatch<AppDispatch>();
    const auth = useSelector((state: RootState) => state.auth);
    const authRef = useRef(auth);
    
    useEffect(() => {
        authRef.current = auth;
    }, [auth]);

    const handleEnded = useCallback((params: any) => {
      //console.log('on call ended', params);

      if(authRef.current.currentCallData?.id === params.call.id) {
        dispatch(setCurrentCallData(null));
      }
    }, [dispatch]);

    const handleRejected = useCallback((params: any) => {
      if(authRef.current.currentCallData?.id === params.call.id && params.user.id !== params.call.created_by.id) {
        leaveCall();
      }
    }, [dispatch]);

    const loginGetStream = useCallback(async (plebId: string) => {
        // Get fresh stream token for this user
        const streamToken = await authRepository.refreshGetStreamToken();
        
        // Connect user with the new token
        const { chatClient, videoClient } = await streamService.connectUser(plebId, streamToken);
    
        const updateUnreadCount = async () => {
          try {
            const response = await chatClient.getUnreadCount(plebId);
            if (response.total_unread_count !== undefined) {
              dispatch(setNbUnread(response.total_unread_count));
            }
          }
          catch(error){
            console.error(error);
          }
        }
    
        await updateUnreadCount();
    
        intervalRef.current = setInterval(async () => {
          await updateUnreadCount();
        }, 20000);
    
        chatClient.on((event) => {    
          if (event.total_unread_count !== undefined) {
            dispatch(setNbUnread(event.total_unread_count));
          }
        });

        videoClient.on('call.ended', handleEnded);
        videoClient.on('call.rejected', handleRejected);
        //videoClient.on('call.accepted', handleAccepted);
    
        return () => {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          videoClient.off('call.ended', handleEnded);
          videoClient.off('call.rejected', handleRejected);
          //videoClient.off('call.accepted', handleAccepted);
        };
      }, [dispatch]);

    const nbUnread = useMemo(() => {
        return auth.currentUserId ? auth.nbUnreadByUser[auth.currentUserId] : 0;
    }, [auth.nbUnreadByUser, auth.currentUserId]);

    const logoutGetStream = useCallback(async () => {
        try {
            // Clear interval first to prevent any pending updates
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            dispatch(setNbUnread(0));

            await streamService.disconnectUser();
            dispatch(setStatusGetStream(AuthenticationStatusGetStream.UNAUTHENTICATED));
        } catch (error) {
            console.error('Error disconnecting stream:', error);
            dispatch(setStatusGetStream(AuthenticationStatusGetStream.ERROR));
        }
    }, [auth.currentUserId, dispatch]);

    const startCall = useCallback(async (currentPlebId: string, plebId: string) => {
      await streamService.startCall(currentPlebId, plebId);
    }, []);

    const leaveCall = useCallback(async () => {
      await streamService.leaveCall();
      dispatch(setCurrentCallData(null));
    }, []);

    const onJoinCall = useCallback((currentPlebId: string, call: Call) => {
      streamService.setCurrentCall(call);
      dispatch(setCurrentCallData(getCallStreamDataFromCall(call, currentPlebId)));
    }, []);

    return {
        statusGetStream: auth.statusGetStream,
        loginGetStream: loginGetStream,
        nbUnread: nbUnread,
        logoutGetStream: logoutGetStream,
        currentCallData: auth.currentCallData,
        setCurrentCallData: useCallback((callStreamData: CallStreamData | null) => {
          dispatch(setCurrentCallData(callStreamData));
        }, [dispatch]),
        addIdsCallsIgnored: useCallback((id: string) => {
          dispatch(addIdsCallsIgnored(id));
        }, [dispatch]),
        removeIdsCallsIgnored: useCallback((id: string) => {
          dispatch(removeIdsCallsIgnored(id));
        }, [dispatch]),
        startCall: startCall,
        leaveCall: leaveCall,
        onJoinCall: onJoinCall,
        idsCallsIgnored: auth.idsCallsIgnored,
    };
}